<script setup lang="ts">
// The console's navigation, in Arco Design Pro's light menu — the admin
// console's, with the customer's pages: icons on the groups, children
// indented under the group's text. App.vue renders it in the side bar, or,
// on a narrow screen, in a drawer opened from the navbar — the way Arco
// Pro does it below its lg breakpoint.
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import MenuIcon from '@/components/icons/MenuIcon.vue'
import { ticketSummary, watchTickets } from '@/store/tickets'
import { useAuthStore } from '@/store/auth'

const props = defineProps<{
  collapsed: boolean
  /** The 收起 row at the foot of the menu. Not in the drawer. */
  collapsible: boolean
}>()
const emit = defineEmits<{ collapse: [boolean] }>()

// 展开菜单's tooltip, closed by the click that expands the menu.
const foldTip = ref(false)
function toggleFold() {
  foldTip.value = false
  emit('collapse', !props.collapsed)
}

const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const go = (name: string) => router.push({ name })
// 子用户 is the main account's to manage: a sub-user does not see it.
const auth = useAuthStore()

// A page under a menu entry (a site's own pages under 站点列表) keeps
// that entry lit, so the menu still says where you are.
const PARENT: Record<string, string> = {
  'domain-detail': 'domains',
  rules: 'domains',
  'ticket-detail': 'tickets',
}
const selected = () => PARENT[route.name as string] ?? (route.name as string)

// 我的工单 says how many tickets support has answered since they were
// last opened.
watchTickets()
const unread = computed(() => ticketSummary.value?.unread ?? 0)
</script>

<template>
  <div class="side">
  <a-menu
    class="side-menu"
    :selected-keys="[selected()]"
    :collapsed="collapsed"
    :show-collapse-button="false"
    auto-open-selected
    :level-indent="34"
  >
    <a-menu-item key="dashboard" @click="go('dashboard')">
      <template #icon><MenuIcon name="dashboard" /></template>
      {{ t('nav.dashboard') }}
    </a-menu-item>
    <a-sub-menu key="grp-sites">
      <template #icon><MenuIcon name="sites" /></template>
      <template #title>{{ t('menu.grpSites') }}</template>
      <a-menu-item key="domains" @click="go('domains')">{{ t('nav.domains') }}</a-menu-item>
      <a-menu-item key="certificates" @click="go('certificates')">{{ t('certificates.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-l4">
      <template #icon><MenuIcon name="l4" /></template>
      <template #title>{{ t('menu.grpL4') }}</template>
      <a-menu-item key="l4-rules" @click="go('l4-rules')">{{ t('l4.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-security">
      <template #icon><MenuIcon name="security" /></template>
      <template #title>{{ t('menu.grpSecurity') }}</template>
      <a-menu-item key="ip-lists" @click="go('ip-lists')">{{ t('ipLists.title') }}</a-menu-item>
      <a-menu-item key="security-events" @click="go('security-events')">{{ t('security.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-billing">
      <template #icon><MenuIcon name="packages" /></template>
      <template #title>{{ t('menu.grpBilling') }}</template>
      <a-menu-item key="package-store" @click="go('package-store')">{{ t('store.title') }}</a-menu-item>
      <a-menu-item key="my-packages" @click="go('my-packages')">{{ t('myPackages.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-finance">
      <template #icon><MenuIcon name="finance" /></template>
      <template #title>{{ t('menu.grpFinance') }}</template>
      <a-menu-item key="billing" @click="go('billing')">{{ t('billing.title') }}</a-menu-item>
      <a-menu-item key="finance-ledger" @click="go('finance-ledger')">{{ t('ledger.title') }}</a-menu-item>
      <a-menu-item key="finance-orders" @click="go('finance-orders')">{{ t('orders.title') }}</a-menu-item>
      <a-menu-item key="finance-withdraw" @click="go('finance-withdraw')">{{ t('withdraw.title') }}</a-menu-item>
      <a-menu-item key="finance-invoices" @click="go('finance-invoices')">{{ t('invoice.title') }}</a-menu-item>
      <a-menu-item key="finance-refunds" @click="go('finance-refunds')">{{ t('refund.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-tickets">
      <template #icon><MenuIcon name="tickets" /></template>
      <template #title>{{ t('menu.grpTickets') }}</template>
      <a-menu-item key="tickets" @click="go('tickets')">
        {{ t('tickets.title') }}<a-badge v-if="unread" :count="unread" class="count" />
      </a-menu-item>
      <a-menu-item key="complaints" @click="go('complaints')">{{ t('complaints.title') }}</a-menu-item>
    </a-sub-menu>
    <a-sub-menu key="grp-account">
      <template #icon><MenuIcon name="account" /></template>
      <template #title>{{ t('menu.grpAccount') }}</template>
      <a-menu-item key="account-center" @click="go('account-center')">{{ t('accountCenter.title') }}</a-menu-item>
      <a-menu-item key="account-security" @click="go('account-security')">{{ t('securityCenter.title') }}</a-menu-item>
      <a-menu-item v-if="!auth.user?.sub_user" key="sub-users" @click="go('sub-users')">{{ t('subUsers.title') }}</a-menu-item>
      <a-menu-item key="credentials" @click="go('credentials')">{{ t('credentials.title') }}</a-menu-item>
    </a-sub-menu>
  </a-menu>
  <!-- 收起, Tencent Cloud's: the menu's own foot, under a rule — its icon
       and word where the menu's icons and titles are; collapsed, the icon
       alone, centred under the icons, its arrow turned round. -->
  <!-- 展开菜单 on hover while collapsed; held in hand, so the click that
       expands the menu takes it away (a disabled tooltip left open stayed
       on screen until the next click elsewhere). -->
  <a-tooltip
    v-if="collapsible"
    :content="t('app.expandMenu')"
    position="right"
    :popup-visible="foldTip && collapsed"
    @popup-visible-change="(v: boolean) => (foldTip = v)"
  >
    <button
      type="button"
      class="fold"
      :class="{ collapsed }"
      :aria-label="collapsed ? t('app.expandMenu') : t('app.collapseMenu')"
      @click="toggleFold"
    >
      <svg class="fold-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round" aria-hidden="true">
        <rect x="3.4" y="4.4" width="17.2" height="15.2" rx="1.6" />
        <path d="M9.2 4.4v15.2" />
        <path :d="collapsed ? 'M13.3 9.4 15.9 12l-2.6 2.6' : 'M16.1 9.4 13.5 12l2.6 2.6'" stroke-linecap="round" />
      </svg>
      <span class="fold-text">{{ t('app.foldMenu') }}</span>
    </button>
  </a-tooltip>
  </div>
</template>

<style scoped>
/* The column: the menu, scrolling on its own, over the 收起 row at its
   foot, which stays put. */
.side {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.side-menu {
  flex: 1;
  min-height: 0;
}
/* The 收起 row: the menu's item rhythm — its icon at the icons' 20px, its
   word at the titles' 54px — under the navbar's rule. At 48px the icon
   alone, at the collapsed icons' 15px, the word narrowing away with the
   side bar's own transition. */
.fold {
  display: flex;
  flex: none;
  align-items: center;
  gap: 16px;
  width: 100%;
  height: 48px;
  padding: 0 0 0 20px;
  overflow: hidden;
  border: none;
  border-top: 1px solid var(--color-border);
  background: none;
  color: var(--color-text-2);
  font: inherit;
  font-size: 14px;
  cursor: pointer;
  transition:
    padding 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    gap 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    color 0.15s,
    background-color 0.15s;
}
.fold:hover {
  color: var(--color-text-1);
  background-color: var(--color-fill-2);
}
.fold.collapsed {
  gap: 0;
  padding-left: 15px;
}
.fold-icon {
  flex: none;
  width: 18px;
  height: 18px;
}
.fold-text {
  max-width: 160px;
  overflow: hidden;
  white-space: nowrap;
  transition:
    max-width 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    opacity 0.2s cubic-bezier(0.34, 0.69, 0.1, 1);
}
.fold.collapsed .fold-text {
  max-width: 0;
  opacity: 0;
}
/* A group's pages, as Tencent Cloud draws them: a hairline down from the
   group's icon, centred on it — the list sits 8px in, the icon's centre
   is 21px further (12px of padding, half of 18px), measured at 29px — and
   on it a short bar for the page under the pointer and the open one.
   Both above the items' grounds, the bar over the line. */
:deep(.arco-menu-inline-content) {
  position: relative;
}
:deep(.arco-menu-inline-content)::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  z-index: 1;
  left: 20.5px;
  width: 1px;
  background: var(--color-border-2);
}
/* The bar follows the pointer, as Tencent Cloud's does: every page has
   one, folded flat; the one under the pointer unfolds in grey with Arco's
   hover ground, the open one stays unfolded in its title's blue. */
:deep(.arco-menu-inline-content .arco-menu-item)::after {
  content: '';
  position: absolute;
  z-index: 2;
  top: 10px;
  bottom: 10px;
  left: 20px;
  width: 2px;
  background: var(--color-text-3);
  transform: scaleY(0);
  transition:
    transform 0.2s cubic-bezier(0.34, 0.69, 0.1, 1),
    background-color 0.2s;
}
:deep(.arco-menu-inline-content .arco-menu-item:hover)::after {
  transform: scaleY(1);
}
:deep(.arco-menu-inline-content .arco-menu-selected)::after {
  transform: scaleY(1);
  background: rgb(var(--primary-6));
}
/* Arco Pro's menu (src/components/menu): 18px icons — Arco's own default
   is the 14px of the text beside them — with the group headers' icon and
   title centred on one line. The expand arrow keeps its size. */
:deep(.arco-menu-inner) .arco-menu-inline-header {
  display: flex;
  align-items: center;
}
:deep(.arco-menu-inner) .arco-icon:not(.arco-icon-down) {
  font-size: 18px;
}
/* Arco Pro's menu scrollbar (its default layout's .menu-wrapper): a thin
   rounded thumb in the text's lightest grey, inset in a 12px track, there
   whenever the menu is taller than the screen. */
:deep(.arco-menu-inner)::-webkit-scrollbar {
  width: 12px;
  height: 4px;
}
:deep(.arco-menu-inner)::-webkit-scrollbar-thumb {
  border: 4px solid transparent;
  border-radius: 7px;
  background-clip: padding-box;
  background-color: var(--color-text-4);
}
:deep(.arco-menu-inner)::-webkit-scrollbar-thumb:hover {
  background-color: var(--color-text-3);
}
/* The open group's icon in the primary blue, its facet a lighter blue —
   as Arco tints the title beside it (see MenuIcon for the greys). */
:deep(.arco-menu-selected) {
  --mi-main: rgb(var(--primary-6));
  --mi-sub: rgb(var(--primary-3));
}
/* Collapsed to 48px, the icons are centred: Arco's 12px item padding
   centres its own 16px icons, so these sat a pixel right of the middle,
   and of the logo above them in the navbar. */
.arco-menu-collapsed :deep(.arco-menu-item),
.arco-menu-collapsed :deep(.arco-menu-pop-header) {
  padding-left: 11px;
}
/* A count of what waits, at the item's right edge — out of the line, which
   is the indent and the text inline, so it never pushes the text down. */
:deep(.arco-menu-item) {
  position: relative;
}
.count {
  position: absolute;
  top: 50%;
  right: 16px;
  line-height: 1;
  transform: translateY(-50%);
}
.count :deep(.arco-badge-number) {
  box-shadow: none;
}
</style>
